import React from 'react';
import DOMPurify from 'dompurify';

/**
 * Replaces slot token patterns in text nodes with styled <span> elements,
 * while preserving all markdown features (links, bold, lists, etc.).
 * Recursively processes all props of React elements for full compatibility.
 */
export default function SlotTokenRenderer({
  children
}: {
  children?: React.ReactNode;
}): React.ReactElement {
  function replaceSlotsInString(text: string): React.ReactNode[] {
    const combinedPattern =
      /\$\{intentContext\.slots\['([^']+)'\]\}|\$\{intentContext\.slots\.([^}\s]+)\}|\$\{chatbotContext\.system\.([^}\s]+)\}|\{([^}]+)\}/g;
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null = null;

    // eslint-disable-next-line no-cond-assign
    while ((match = combinedPattern.exec(text)) !== null) {
      const { index } = match;
      if (index > lastIndex) {
        nodes.push(text.slice(lastIndex, index));
      }
      const slotName = match[1] || match[2] || match[3] || match[4] || '';
      nodes.push(
        <span key={index} className='slot-chip'>
          {slotName}
        </span>
      );
      lastIndex = combinedPattern.lastIndex;
    }
    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex));
    }
    return nodes;
  }

  function process(node: React.ReactNode): React.ReactNode {
    if (typeof node === 'string') {
      const sanitised = DOMPurify.sanitize(node, {
        ALLOWED_TAGS: [],
        KEEP_CONTENT: true
      });
      return replaceSlotsInString(sanitised);
    }
    if (React.isValidElement(node)) {
      const newProps: Record<string, any> = {};
      Object.entries(node.props).forEach(([key, value]) => {
        if (
          key === 'children' ||
          React.isValidElement(value) ||
          Array.isArray(value) ||
          typeof value === 'string'
        ) {
          newProps[key] = process(value as React.ReactNode);
        } else {
          newProps[key] = value;
        }
      });
      return React.cloneElement(node, newProps);
    }
    if (Array.isArray(node)) {
      return node.map(process);
    }
    return node;
  }

  return <>{process(children)}</>;
}
